import React, { Fragment } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { Box, Grid, Inline, Text } from '@atlaskit/primitives/compiled';
import { TabPanel } from '@atlaskit/tabs';
import Tag from '@atlaskit/tag';

import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import type { QueryIntent } from '../../../constants';
import { CopyButton } from '../../common/ui/copy-button';
import { messages } from '../messages';
import type { RequestOverviewData } from '../types';
import { getPluginTagColor } from '../utils';

import { Panel } from './panel-wrapper';

const styles = cssMap({
	twoColumnGrid: {
		gridTemplateColumns: '1fr 1fr',
	},
});

type RequestOverviewPanelProps = RequestOverviewData & {
	smartAnswersRoute: QueryIntent | undefined;
	isNaturalLanguageQuery: boolean;
};

export const RequestOverviewPanel: ({
	originalQuery,
	transformedQuery,
	smartAnswersRoute,
	traceId,
	modelName,
	cacheHit,
	sources,
	isNaturalLanguageQuery,
	invokedPluginNames,
}: RequestOverviewPanelProps) => React.JSX.Element = ({
	originalQuery,
	transformedQuery,
	smartAnswersRoute,
	traceId,
	modelName,
	cacheHit,
	sources,
	isNaturalLanguageQuery,
	invokedPluginNames,
}: RequestOverviewPanelProps) => {
	const { formatMessage } = useIntl();

	const displayCacheHit = cacheHit === null ? 'disabled' : cacheHit.toString();

	return (
		<TabPanel>
			<Panel testId="sain-online-debugger-request-overview-panel">
				<Box>
					<Heading size="xsmall">
						{formatMessage(messages.ai_debugger_modal_original_query_subheading)}
					</Heading>
					<Text>{originalQuery ?? 'null'}</Text>
				</Box>
				<Box>
					<Heading size="xsmall">
						{formatMessage(messages.ai_debugger_modal_transformed_query_subheading)}
					</Heading>
					<Text>{transformedQuery ?? 'null'}</Text>
				</Box>
				<Grid xcss={styles.twoColumnGrid} gap="space.100">
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_detected_intent_subheading)}
						</Heading>
						<Text>{smartAnswersRoute}</Text>
					</Box>
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_trace_id_subheading)}
						</Heading>
						<Inline space="space.100" alignBlock="start">
							<Text>{traceId ?? 'null'}</Text>
							{traceId && (
								<CopyButton
									copyText={traceId}
									copyLabel={formatMessage(messages.ai_debugger_modal_copy_trace_id_label)}
									copiedLabel={formatMessage(messages.ai_debugger_modal_trace_id_copied_text)}
									testId="sain-online-debugger-copy-trace-id-button"
								/>
							)}
						</Inline>
					</Box>
					{isNaturalLanguageQuery && (
						<Fragment>
							<Box>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_model_subheading)}
								</Heading>
								<Text>{modelName}</Text>
							</Box>
							<Box>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_cached_answer_subheading)}
								</Heading>
								<Text>{displayCacheHit}</Text>
							</Box>
						</Fragment>
					)}
				</Grid>
				{/* Plugins invoked - only show if there are plugins */}
				{isNaturalLanguageQuery && invokedPluginNames.length > 0 && (
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_plugins_invoked_subheading)}
						</Heading>
						<Inline space="space.100" alignBlock="center">
							{invokedPluginNames.map((pluginName) => (
								<Tag
									key={pluginName}
									text={pluginName}
									color={getPluginTagColor(pluginName)}
									isRemovable={false}
									migration_fallback="lozenge"
								/>
							))}
						</Inline>
					</Box>
				)}
				{/* Sources used - only show if there are sources */}
				{isNaturalLanguageQuery && sources.length > 0 && (
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_sources_subheading)}
						</Heading>
						{sources.map((source: NLPSearchResultSource, index: number) => (
							<Inline key={source.ari ?? source.id} space="space.050" alignBlock="center">
								<Text>{index + 1}.</Text>
								<Link href={source.url} target="_blank">
									{source.title}
								</Link>
							</Inline>
						))}
					</Box>
				)}
			</Panel>
		</TabPanel>
	);
};
